import React from "react";
import Modal from "react-modal";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { Receipt, ReceiptType, ReceiptUpdate } from "../../../models";
import { updateReceipt } from "../receiptsSlice";
import { getVehicles } from "../../Vehicles/vehiclesSlice";

import "./EditReceiptModal.scss";

const modalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#FFFFFF",
    maxWidth: "420px",
    width: "90%",
    padding: "0",
  },
};

interface Props {
  // The receipt being edited; null = modal closed.
  receipt: Receipt | null;
  onClose: () => void;
  // Called after a successful save so the caller can refetch the list.
  onSaved: () => void;
}

// Converts a receipt ISO date string ("2026-10-20T00:00:00Z") to an HTML date
// input value ("2026-10-20"). Returns "" if the input is null/empty.
function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return "";
  return iso.slice(0, 10);
}

// Edit fields: cost, type (gas/misc), truck (vehicle_id), receipt_date, note.
// Only fields that changed from their original value are sent in the PATCH.
export function EditReceiptModal({ receipt, onClose, onSaved }: Props) {
  const dispatch = useAppDispatch();
  const vehicles = useAppSelector((s) => s.vehicles.vehicles);

  const [cost, setCost] = React.useState("");
  const [receiptType, setReceiptType] = React.useState<ReceiptType | "">("");
  const [vehicleId, setVehicleId] = React.useState("");
  const [receiptDate, setReceiptDate] = React.useState("");
  const [note, setNote] = React.useState("");

  // Track the original values so we can diff them on save.
  const origRef = React.useRef<{
    cost: string;
    receiptType: string;
    vehicleId: string;
    receiptDate: string;
    note: string;
  }>({ cost: "", receiptType: "", vehicleId: "", receiptDate: "", note: "" });

  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Populate vehicle dropdown once on open.
  React.useEffect(() => {
    dispatch(getVehicles({ withStats: false }));
  }, [dispatch]);

  // Prefill from the receipt each time the modal opens.
  React.useEffect(() => {
    if (!receipt) return;
    const origCost = receipt.cost != null ? String(receipt.cost) : "";
    const origType = receipt.receiptType ?? "";
    const origVehicle = receipt.vehicleId ?? "";
    const origDate = toDateInputValue(receipt.receiptDate);
    const origNote = receipt.note ?? "";

    setCost(origCost);
    setReceiptType(origType as ReceiptType | "");
    setVehicleId(origVehicle);
    setReceiptDate(origDate);
    setNote(origNote);
    setError(null);

    origRef.current = {
      cost: origCost,
      receiptType: origType,
      vehicleId: origVehicle,
      receiptDate: origDate,
      note: origNote,
    };
  }, [receipt]);

  const save = async () => {
    if (!receipt) return;

    const update: ReceiptUpdate = {};
    const orig = origRef.current;

    // Cost
    if (cost.trim() !== orig.cost) {
      if (cost.trim() === "") {
        // cleared — keep as undefined (omit); if caller wants null, extend ReceiptUpdate
      } else {
        const n = Number(cost);
        if (isNaN(n) || n < 0) {
          setError("Cost must be a non-negative number.");
          return;
        }
        update.cost = n;
      }
    }

    // Type
    if (receiptType !== orig.receiptType && receiptType !== "") {
      update.receiptType = receiptType as ReceiptType;
    }

    // Vehicle
    if (vehicleId !== orig.vehicleId) {
      if (vehicleId !== "") {
        update.vehicleId = vehicleId;
      }
    }

    // Receipt date
    if (receiptDate !== orig.receiptDate) {
      if (receiptDate !== "") {
        update.receiptDate = receiptDate;
      }
    }

    // Note
    if (note !== orig.note) {
      update.note = note;
    }

    setSaving(true);
    setError(null);
    try {
      await dispatch(
        updateReceipt({ id: receipt.id, update })
      ).unwrap();
      onSaved();
      onClose();
    } catch {
      setError("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={receipt !== null}
      onRequestClose={onClose}
      style={modalStyles}
      contentLabel="Edit receipt"
    >
      <div className="edit-receipt-modal">
        <div className="edit-receipt-header">
          <h3>Edit receipt</h3>
          <button
            className="edit-receipt-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="edit-receipt-body">
          <label className="edit-field">
            <span>Cost ($)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              placeholder="—"
            />
          </label>
          <label className="edit-field">
            <span>Type</span>
            <select
              value={receiptType}
              onChange={(e) =>
                setReceiptType(e.target.value as ReceiptType | "")
              }
            >
              <option value="">— select —</option>
              <option value="gas">Gas</option>
              <option value="misc">Misc</option>
            </select>
          </label>
          <label className="edit-field">
            <span>Truck</span>
            <select
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
            >
              <option value="">— none —</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label}
                </option>
              ))}
            </select>
          </label>
          <label className="edit-field">
            <span>Receipt date</span>
            <input
              type="date"
              value={receiptDate}
              onChange={(e) => setReceiptDate(e.target.value)}
            />
          </label>
          <label className="edit-field">
            <span>Note</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="—"
            />
          </label>
          {error && <div className="edit-error">{error}</div>}
        </div>
        <div className="edit-receipt-footer">
          <button
            className="btn btn-cancel"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button className="btn btn-save" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
